// Inline SVG icon set. We keep the icons in code (no font, no sprite, no
// external image fetch) so the offline PWA install bundles them all. Each
// entry in `I` is a function so callers can pass an override color cheaply.
import { T } from "./theme.js";

const Icon = ({ d, size = 20, color = T.textSec, fill = "none", sw = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color}
       strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

// Filled-path icon — for the SF-style glyphs from the design system
// where the silhouette is a single compound path drawn with even-odd
// fill rule. The fill inherits via `currentColor` so the parent
// button's `color` prop tints the icon (active = persimmon, inactive
// = ink56, etc.). No stroke — these glyphs depend on internal cut-outs
// for their detail.
const SolidIcon = ({ d, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path fillRule="evenodd" clipRule="evenodd" d={d} />
  </svg>
);

export const I = {
  search: (c) => <Icon d="M11 4a7 7 0 110 14 7 7 0 010-14zM21 21l-4.3-4.3" color={c} />,
  send: (c = T.accent) => <svg width="24" height="24" viewBox="0 0 24 24" fill={c}><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>,
  back: () => <Icon d="M19 12H5M12 19l-7-7 7-7" color={T.text} />,
  plus: (c = T.accent) => <Icon d="M12 5v14M5 12h14" color={c} />,
  close: () => <Icon d="M18 6L6 18M6 6l12 12" />,
  users: (c = T.textSec) => <Icon d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" color={c} />,
  mail: (c = T.textSec) => <Icon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6" color={c} />,
  lock: (c = T.textSec) => <Icon d="M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4" color={c} />,
  hash: (c = T.accent) => <Icon d="M4 9h16M4 15h16M10 3l-2 18M16 3l-2 18" color={c} size={18} />,
  // ─── Sidebar action-row glyphs — Set E (alt metaphors) ───────────
  // Bag · 3D box · wallet card · sliders · person+shield · power button.
  // All inherit color from the parent button via `currentColor`. Size
  // defaults to 20 to fit the 36×36 action buttons.
  // Bell — used in the sidebar header for the notifications feed.
  // Same Set E filled-glyph treatment as the other action icons.
  // Style-E bell with a separate clapper dot — replaces the earlier
  // single-glyph bell. Path supplied by the design pass (see
  // BuyerGram Notifications Icon.html); fillRule/clipRule are applied
  // by SolidIcon, so the path itself stays as-is.
  bell: (size = 20) => (
    <SolidIcon size={size} d="M12 2a2 2 0 011.95 1.57A6.5 6.5 0 0118.5 10v3.2l1.3 2.3a1 1 0 01-.87 1.5H14.9a3 3 0 01-5.8 0H5.07a1 1 0 01-.87-1.5l1.3-2.3V10a6.5 6.5 0 014.55-6.43A2 2 0 0112 2zm0 4a4.5 4.5 0 00-4.5 4.5v3.46a1 1 0 01-.13.49L6.78 15h10.44l-.59-1.05a1 1 0 01-.13-.49V10.5A4.5 4.5 0 0012 6zm-1.2 11a1 1 0 002.4 0h-2.4z" />
  ),
  store: (size = 20) => (
    <SolidIcon size={size} d="M8 6a4 4 0 118 0h3.2l1 14H3.8l1-14H8zm2 0h4a2 2 0 10-4 0zM6.7 8l-.7 10h12l-.7-10H6.7z" />
  ),
  cart: (size = 20) => (
    <SolidIcon size={size} d="M3 3h2.2a1 1 0 01.98.8L6.5 5H20a1 1 0 01.96 1.27l-1.8 6.3a1.5 1.5 0 01-1.44 1.08H8.1l.3 1.5h9.1v1.85H7.6a1 1 0 01-.98-.8L4.1 4.85H3V3zm5.5 16a1.6 1.6 0 110 3.2 1.6 1.6 0 010-3.2zm9 0a1.6 1.6 0 110 3.2 1.6 1.6 0 010-3.2z" />
  ),
  orders: (size = 20) => (
    <SolidIcon size={size} d="M12 2l9 4.5v11L12 22l-9-4.5v-11L12 2zm0 2.2L5.6 7.4 12 10.6l6.4-3.2L12 4.2zM5 9v7.3l6 3v-7.2L5 9zm14 0l-6 3.1v7.2l6-3V9z" />
  ),
  wallet: (size = 20) => (
    <SolidIcon size={size} d="M3 6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6zm2 0v2h14V6H5zm0 5v7h14v-7H5zm2 4h5v2H7v-2z" />
  ),
  settings: (size = 20) => (
    <SolidIcon size={size} d="M3 5h7.2a3 3 0 015.6 0H21v2h-5.2a3 3 0 01-5.6 0H3V5zm10 0a1 1 0 100 2 1 1 0 000-2zM3 11h2.2a3 3 0 015.6 0H21v2h-10.2a3 3 0 01-5.6 0H3v-2zm5 0a1 1 0 100 2 1 1 0 000-2zm5 6h8v2h-8v-2zm-2-1a3 3 0 010 6 3 3 0 010-6zm0 2a1 1 0 100 2 1 1 0 000-2zM3 17h4v2H3v-2z" />
  ),
  // Help / FAQ — question-mark-in-circle, Set E. Path supplied by the
  // design pass (see BuyerGram Help-FAQ Icon.html).
  help: (size = 20) => (
    <SolidIcon size={size} d="M12 2a10 10 0 110 20 10 10 0 010-20zm0 2a8 8 0 100 16 8 8 0 000-16zm.2 3a3.3 3.3 0 013 4.7c-.3.6-.8 1-1.3 1.4-.5.4-.8.6-1 .9-.15.25-.2.5-.2.9h-2c0-.7.15-1.25.5-1.75.3-.45.75-.8 1.15-1.1.35-.27.6-.45.75-.75A1.3 1.3 0 1010.8 9.6l-2-.3A3.3 3.3 0 0112.2 7zM11 15.5h2v2h-2v-2z" />
  ),
  // shield: person silhouette with a small shield overlay — admin
  // identity glyph. Used in the action row at 20px and also in the
  // small inline badge (see `admin` key) at 14px.
  shield: (size = 20) => (
    <SolidIcon size={size} d="M9 2a4 4 0 110 8 4 4 0 010-8zm0 2a2 2 0 100 4 2 2 0 000-4zm8.5 3l3.5 1.5V11c0 2.5-1.6 4.2-3.5 5-1.9-.8-3.5-2.5-3.5-5V8.5L17.5 7zm0 2.2l-1.5.6V11c0 1.3.7 2.2 1.5 2.7.8-.5 1.5-1.4 1.5-2.7V9.8l-1.5-.6zM9 13c2.4 0 4.5 1 5.7 2.6-.3.9-.4 1.2-.4 2.4v2H2v-2c0-3 3-5 7-5z" />
  ),
  trash: (c = T.danger) => <Icon d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" color={c} size={16} />,
  mic: () => <Icon d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />,
  attach: () => <Icon d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />,
  emoji: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.textSec} strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="2.5"/><line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="2.5"/></svg>,
  group: (c = T.accent) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/><path d="M16 3.13a4 4 0 010 7.75M21 21v-2a4 4 0 00-3-3.85"/></svg>,
  // admin: small 14×14 inline badge next to the user's display name.
  // Same path as the action-row shield (Set E person + shield overlay)
  // for visual consistency — just rendered tighter.
  admin: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={T.accent}>
      <path fillRule="evenodd" clipRule="evenodd" d="M9 2a4 4 0 110 8 4 4 0 010-8zm0 2a2 2 0 100 4 2 2 0 000-4zm8.5 3l3.5 1.5V11c0 2.5-1.6 4.2-3.5 5-1.9-.8-3.5-2.5-3.5-5V8.5L17.5 7zm0 2.2l-1.5.6V11c0 1.3.7 2.2 1.5 2.7.8-.5 1.5-1.4 1.5-2.7V9.8l-1.5-.6zM9 13c2.4 0 4.5 1 5.7 2.6-.3.9-.4 1.2-.4 2.4v2H2v-2c0-3 3-5 7-5z"/>
    </svg>
  ),
  // logout: door frame with an arrow pointing out — borrowed from the
  // first Apple/SF set because it reads more clearly as "leave" than
  // Set E's power-button glyph (the latter could be misread as
  // "shutdown the app" / "off").
  logout: (size = 20) => (
    <SolidIcon size={size} d="M5 3h6v2H6v14h5v2H5a1 1 0 01-1-1V4a1 1 0 011-1zm9.6 4.4l1.4-1.4L21.4 12 16 17.4l-1.4-1.4 3-3H9v-2h8.6l-3-3z" />
  ),
};
